import { Building2, Grid2x2, Grid3x3, SlidersHorizontal, SquareDashed, type LucideIcon } from 'lucide-react'
import type { DemoSize } from '../index.js'

/** One picture per demo dataset, by how much is in it: an empty frame, a small grid, a full grid, a building for an operator's worth of facilities. */
const DEMO_SIZE: Record<DemoSize, { Icon: LucideIcon; label: string; detail: string }> = {
  blank: { Icon: SquareDashed, label: 'Blank demo', detail: 'Blank: the client alone, with no facilities or workers' },
  minimum: { Icon: Grid2x2, label: 'Minimum demo', detail: 'Minimum: one facility with its administrator and first workers' },
  standard: { Icon: Grid3x3, label: 'Standard demo', detail: 'Standard: the whole fixed dataset' },
  large: { Icon: Building2, label: 'Large demo', detail: 'Large: the standard dataset at scale, with 316 facilities and ten operators' },
  custom: { Icon: SlidersHorizontal, label: 'Custom demo', detail: 'Custom: facilities, units, staffing, history and months ahead set on the form' },
}

/** What the detail panel says about a demo's dataset; older demos predate the record. */
export function demoSizeLabel(size: DemoSize | null): string {
  return size ? DEMO_SIZE[size].detail : 'Demo client; dataset not recorded'
}

/**
 * The Demo tag on a client row, with the dataset's picture when the
 * registration recorded one. The picture is named in the tag's title and
 * for screen readers, so the kind of demo reads from the row without
 * opening it.
 */
export function DemoSizeBadge({ size }: { size: DemoSize | null }) {
  const entry = size ? DEMO_SIZE[size] : null
  return (
    <span
      title={entry?.detail ?? 'Demo client'}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-coral-lt text-coral align-middle"
    >
      {entry && <entry.Icon aria-hidden="true" strokeWidth={2} className="h-3 w-3 shrink-0" />}
      Demo
      {entry && <span className="sr-only">, {entry.label}</span>}
    </span>
  )
}
